import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, take } from 'rxjs/operators';
import { CustomerModel } from '../_models/customer.model';

import { environment } from '../../../../environments/environment';
import { UserService } from '../../auth/_services/user.service';
import { PRE_LOGIN_ENV_FIELDS } from '../../common/constants/public-info-fields.constants';

const API_CUSTOMER_URL = '/api/enterprise';
const API_URL = environment.API_URL;


@Injectable({
  providedIn: 'root'
})
export class CustomerService {

    customerList: any[];
    customerListChanged = new EventEmitter<any[]>();
    customerProjectionFields: string = [ "_id", "location", "zoomLevel", "name", "logo", "type", "business", "notes", "timezone", "currency" ].join(",");
    constructor(private readonly http: HttpClient,
      private readonly userService: UserService) { }

    private enterpriseId: string | null = null;
    private enterpriseId$: Observable<string> | null = null;

    // Cached for the lifetime of the app session so every caller reuses the
    // same enterpriseId instead of re-fetching it from /api/enterprise.
    fetchEnterpriseId(): Observable<string> {
      if (this.enterpriseId) {
        return of(this.enterpriseId);
      }
      if (!this.enterpriseId$) {
        this.enterpriseId$ = this.getCustomer(true, '_id').pipe(
          take(1),
          map((res: any) => {
            this.enterpriseId = res.data?.[0]?._id;
            return this.enterpriseId;
          }),
          shareReplay(1),
          catchError(err => {
            this.enterpriseId$ = null;
            this.enterpriseId = null;
            return throwError(() => err);
          })
        );
      }
      return this.enterpriseId$;
    }

    emptyCustomer(){
      this.customerList = [];
      this.customerListChanged.emit([]);
    }

    getCustomer(observableFlag = false, fields = null) {
      let url = API_URL + API_CUSTOMER_URL;
      let httpParams = new HttpParams();

      if (fields) {
        httpParams = httpParams.set('fields', fields);
      }

      if (observableFlag) {
        return this.http.get(url, { params: httpParams, headers: this.userService.getHttpAuthHeader() });
      }

      this.http.get(url, { params: httpParams, headers: this.userService.getHttpAuthHeader() }).subscribe((data: any) => {
        this.customerList = data.data;
        this.customerListChanged.emit(data.data);
      });
    }

    createCustomer(c: Partial<CustomerModel>, observableFlag = false) {

      let customer = {
        location: c.location,
        type: c.type,
        name: c.name.trim(),
        logo: c.logo.trim(),
        business: c.business,
        notes: c.notes,
        timezone: c.timezone,
        currency: c.currency,
        //organization: c.organization
        //system: []
      }

      if(observableFlag){
        return this.http.post<CustomerModel>(API_URL + API_CUSTOMER_URL, customer, { headers: this.userService.getHttpAuthHeader() });
      }

      this.http.post<CustomerModel>(API_URL + API_CUSTOMER_URL, customer, { headers: this.userService.getHttpAuthHeader() }).subscribe((data: any) => {
        this.getCustomer(false, this.customerProjectionFields);
      });
    }

    updateCustomer(c: Partial<CustomerModel>, observableFlag = false) {
      let customer = {
        _id: c._id,
        location: c.location,
        type: c.type,
        name: c.name.trim(),
        logo: c.logo.trim(),
        business: c.business,
        notes: c.notes,
        timezone: c.timezone,
        zoomLevel: c.zoomLevel,
        currency: c.currency
        //organization: c.organization
        //system: []
      }
      if(observableFlag){
        return this.http.put<CustomerModel>(API_URL + API_CUSTOMER_URL, customer, { headers: this.userService.getHttpAuthHeader() });
      }

      this.http.put<CustomerModel>(API_URL + API_CUSTOMER_URL, customer, { headers: this.userService.getHttpAuthHeader() }).subscribe((data: any) => {
        this.getCustomer(false, this.customerProjectionFields);
      });
    }

    /* deleteCustomer(id:string) {
      this.http.delete<CustomerModel>(API_URL + API_CUSTOMER_URL + '?_id=' + id, { headers: this.userService.getHttpAuthHeader() }).subscribe((data: any) => {
        this.getCustomer();
      });
    } */

    deleteCustomer(id:string) {
      return this.http.delete<CustomerModel>(API_URL + API_CUSTOMER_URL + '?_id=' + id, { headers: this.userService.getHttpAuthHeader() });
    }

    getPublicInfo(fields = ''){
      if (!fields) {
        fields = [
          'enterpriseInfo.logo',
          'enterpriseInfo._id',
          ...PRE_LOGIN_ENV_FIELDS.map(k => `publicInfo.${k}`)
        ].join(',');
      }
      let url = API_CUSTOMER_URL + '/get-public-info'
      let httpParams = new HttpParams();
      if (fields) {
        httpParams = httpParams.set('fields', fields);
      }
      return this.http.get(API_URL + url, { params: httpParams }) //public URL, no headers
    }

}
